import { useEffect } from "react";

/**
 * Lightweight per-page SEO. Sets document.title + meta description +
 * canonical + Open Graph on mount. Restores to defaults on unmount.
 * (Lighter than react-helmet for our needs.)
 */
export default function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  noIndex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = new Map();

    const setMeta = (selector, attr, value) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        const [attrName, attrVal] = selector
          .replace("meta[", "")
          .replace("]", "")
          .split("=");
        tag.setAttribute(attrName, attrVal.replaceAll('"', ""));
        document.head.appendChild(tag);
      }
      prevMeta.set(selector, tag.getAttribute(attr));
      tag.setAttribute(attr, value);
    };

    const setLink = (rel, href) => {
      let tag = document.head.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      prevMeta.set(`link[rel="${rel}"]`, tag.getAttribute("href"));
      tag.setAttribute("href", href);
    };

    if (title) document.title = title;
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (canonical) {
      setLink("canonical", canonical);
      setMeta('meta[property="og:url"]', "content", canonical);
      setMeta('meta[name="twitter:url"]', "content", canonical);
    }
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }
    setMeta('meta[property="og:type"]', "content", type);
    if (noIndex) {
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
    }

    return () => {
      document.title = prevTitle;
      prevMeta.forEach((prev, selector) => {
        const tag = document.head.querySelector(selector);
        if (!tag || prev == null) return;
        if (selector.startsWith("link")) {
          tag.setAttribute("href", prev);
        } else {
          tag.setAttribute("content", prev);
        }
      });
    };
  }, [title, description, canonical, image, type, noIndex]);

  return null;
}
